<template>
  <div
    class="o-switcher"
    :class="[
      switcherStateClass,
      `variant-${variant}`,
      {
        'text-before': textBefore,
        'text-hide': textHide,
        'is-disabled': disabled
      }
    ]"
  >
    <div class="switcher-text">
      {{ switcherState ? switcherTextOn : switcherTextOff }}
      <div aria-hidden="true">{{ switcherTextOn }}</div>
      <div aria-hidden="true">{{ switcherTextOff }}</div>
    </div>

    <button class="switcher-wrapper" @click.prevent="toggleSwitcher">
      <div class="switcher-on">
        <font-awesome-icon
        icon="check"
        size="xs"
      />
      </div>
      <div class="switcher-handle"></div>
    </button>
  </div>
</template>

<script>
export default {
  name: 'Switcher',
  props: {
    state: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'default'
    },
    textOn: {
      type: String,
      default: ''
    },
    textOff: {
      type: String,
      default: ''
    },
    textBefore: {
      type: Boolean,
      default: false
    },
    textHide: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      switcherState: this.state,
      switcherDisabled: this.disabled
    }
  },
  computed: {
    switcherStateClass() {
      return this.switcherState ? 'state-on' : 'state-off'
    },
    switcherTextOn() {
      return this.textOn || this.$t('components.switcher.stateText.on')
    },
    switcherTextOff() {
      return this.textOff || this.$t('components.switcher.stateText.off')
    }
  },
  methods: {
    toggleSwitcher() {
      if (!this.switcherDisabled) {
        this.switcherState = !this.switcherState
        this.$emit('switcherChange', this.switcherState)
      }
    }
  },
  watch: {
    '$props.state'(newState) {
      this.switcherState = newState
    },
    '$props.disabled'(newDisabled) {
      this.switcherDisabled = newDisabled
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

$switcher-height: 1.4em !default;
$switcher-border-width: 3px !default;
$switcher-border-color: transparent !default;
$switcher-background-color: $brand-primary !default;

$switcher-handle-transition: all 300ms ease-out !default;
$switcher-on-handle-color: black !default;
$switcher-off-handle-color: white !default;
$switcher-off-background-color: $brand-secondary !default;

$switcher-blue-border-color: $brand-blue !default;
$switcher-blue-on-handle-color: $switcher-blue-border-color !default;
$switcher-blue-off-handle-color: rgba(
  $switcher-blue-border-color,
  0.75
) !default;

$switcher-white-border-color: $brand-white !default;
$switcher-white-on-handle-color: $switcher-white-border-color !default;
$switcher-white-off-handle-color: rgba(
  $switcher-white-border-color,
  0.75
) !default;

$switcher-text-gap: 1em !default;

$switcher-disabled-opacity: 0.5 !default;

.o-switcher {
  display: flex;
  align-items: center;

  .switcher-wrapper {
    flex: 0 0 auto;
    display: block;
    position: relative;
    padding: 0;
    width: $switcher-height * 1.75;
    height: $switcher-height;
    background: $switcher-background-color;
    border: $switcher-border-width solid $switcher-border-color;
    font-size: 15px;
    cursor: pointer;
    border-radius: 10em;

    .switcher-handle {
      position: relative;
      margin: $switcher-border-width;
      width: calc(#{$switcher-height} - #{$switcher-border-width * 4});
      height: calc(#{$switcher-height} - #{$switcher-border-width * 4});
      border-radius: 50%;
      transition: $switcher-handle-transition;
    }

    .switcher-on {
      height: 1em;
      line-height: 1px;
      position: absolute;
      top: 2px;
      left: 3px;
      opacity: 1;
      transition: $switcher-handle-transition;
    }

  }

  .switcher-text {
    margin-right: $switcher-text-gap;
    text-align: right;

    div {
      overflow: hidden;
      height: 0;
    }
  }

  &.state-on {
    .switcher-wrapper {
      .switcher-handle {
        background-color: $switcher-on-handle-color;
        transform: translateX($switcher-height * 0.75);
      }
      .switcher-on {
        color: $switcher-on-handle-color;
      }
    }
  }

  &.state-off {
    .switcher-wrapper {
      background: $switcher-off-background-color;

      .switcher-handle {
        background-color: $switcher-off-handle-color;
      }
      .switcher-on {
        color: $switcher-off-handle-color;
        opacity: 0;
      }
    }
  }

  &.variant-blue {
    .switcher-wrapper {
      border-color: $switcher-blue-border-color;
    }

    &.state-on {
      .switcher-wrapper {
        .switcher-handle {
          background-color: $switcher-blue-on-handle-color;
        }
        .switcher-on {
          color: $switcher-blue-on-handle-color;
        }
      }
    }

    &.state-off {
      .switcher-wrapper {
        .switcher-handle {
          background-color: $switcher-blue-off-handle-color;
        }
        .switcher-on {
          color: $switcher-blue-off-handle-color;
          opacity: 0;
        }
      }
    }
  }

  &.variant-primary {
    .switcher-wrapper {
      border-color: $brand-primary;
      background-color: white;
    }

    &.state-on {
      .switcher-wrapper {
        .switcher-handle {
          background-color: $brand-primary;
        }
        .switcher-on {
          color: $brand-primary;
        }
      }
    }

    &.state-off {
      .switcher-wrapper {
        border-color: $brand-secondary;
        .switcher-handle {
          background-color: $brand-secondary;
        }
        .switcher-on {
          color: $brand-primary;
          opacity: 0;
        }
      }
    }
  }

  &.variant-white {
    .switcher-wrapper {
      border-color: $switcher-white-border-color;
    }

    &.state-on {
      .switcher-wrapper {
        .switcher-handle {
          background-color: $switcher-white-on-handle-color;
        }
        .switcher-on {
          color: $switcher-white-on-handle-color;
        }
      }
    }

    &.state-off {
      .switcher-wrapper {
        .switcher-handle {
          background-color: $switcher-white-off-handle-color;
        }
        .switcher-on {
          color: $switcher-white-off-handle-color;
          opacity: 0;
        }
      }
    }
  }

  &.text-before {
    flex-direction: row-reverse;

    .switcher-text {
      margin-left: 0;
      margin-right: $switcher-text-gap;
      text-align: right;
    }
  }

  &.text-hide {
    .switcher-text {
      display: none;
    }
  }

  &.is-disabled {
    opacity: $switcher-disabled-opacity;

    .switcher-wrapper {
      cursor: default;
    }
  }
}
</style>
